import { DateTime } from 'luxon';

export class ObjectUtility {
  /**
   * Converts the properties of an object to strings.
   *
   * This function takes an object and returns a new object where each
   * property value has been converted to a string. If a property value
   * is `null` or `undefined`, it will be omitted from the result.
   * Additionally, if a property value is an instance of `DateTime`,
   * it will be converted to an ISO date string.
   *
   * @param obj - The object whose properties will be converted to strings.
   * @returns A new object with the same keys as the input object,
   *          but with all values converted to strings.
   */
  static convertPropsToString<T extends {}>(
    obj: T,
  ): { [K in keyof T]: string } {
    const result = {} as { [K in keyof T]: string };

    for (const key in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;

      const value = obj[key];

      if (_.isNil(value)) continue;

      if (value instanceof DateTime) {
        result[key] = value.toISODate();
      } else {
        result[key] = String(value);
      }
    }

    return result;
  }

  /**
   * Case-insensitive retrieval of object properties
   * @param object object to parse
   * @param key property to return
   * @returns property if found
   */
  static getPropertyCaseInsensitive(object: any, key: string) {
    const asLowercase = key.toLowerCase();
    const property = Object.keys(object).find(
      (k) => k.toLowerCase() === asLowercase,
    );

    if (property) {
      return object[property];
    }
  }

  /**
   * Deep compare two arrays of the same type to determine if they have the same contents.
   */
  static sequenceEquals<T>(arr1: T[], arr2: T[]) {
    return _(arr1).differenceWith(arr2, _.isEqual).isEmpty();
  }

  // Helper function to recursively update nested properties
  static updateNestedData(existingData: any, newData: any) {
    Object.keys(newData).forEach((propertyName) => {
      const value = newData[propertyName];

      // Skip undefined values
      if (value === undefined) {
        return; // Do nothing if the value is undefined
      }

      // If the property is an object and the existing data at that property is also an object, recurse
      if (typeof value === 'object' && value !== null) {
        if (!existingData[propertyName]) {
          existingData[propertyName] = {}; // Initialize if the property doesn't exist yet
        }
        this.updateNestedData(existingData[propertyName], value); // Recurse into the nested property
      } else {
        // Otherwise, directly assign the value
        existingData[propertyName] = value;
      }
    });
  }

  // Enhanced function to update a property using a path (e.g., "patient.name")
  /**
   * @example
   * // Primitive update
   * const obj = { patient: { name: "John Doe" } };
   * updateProperty(obj, "patient.name", "Jane Doe");
   * console.log(obj.patient.name); // "Jane Doe"
   *
   * // Object update (merging)
   * updateProperty(obj, "patient.address", { city: "Somewhere", street: "Main St" });
   * console.log(obj.patient.address); // { city: "Somewhere", street: "Main St" }
   *
   * // Nested object update
   * updateProperty(obj, "patient.address.zip", { code: 12345 });
   * console.log(obj.patient.address.zip); // { code: 12345 }
   *
   * @remarks
   * - If the specified path doesn't exist, intermediate objects will be created automatically.
   * - If the value is an object, it will merge recursively, not overwrite.
   * - If the value is `undefined`, the update will be skipped.
   */
  static updateProperty<T>(
    target: T,
    path: string,
    value: Record<string, any> | string | number | null | boolean | undefined,
  ) {
    if (!target || !path) {
      return; // Guard against invalid inputs
    }

    const pathParts = path.split('.'); // Split the path into an array of keys

    let currentTarget: any = target;

    // Traverse the object up to the second last path part
    for (let i = 0; i < pathParts.length - 1; i++) {
      const key = pathParts[i];

      if (!currentTarget[key]) {
        currentTarget[key] = {}; // Initialize object if not exists
      }

      currentTarget = currentTarget[key]; // Move deeper into the object
    }

    // Now we are at the last part of the path, set the value
    const lastKey = pathParts[pathParts.length - 1];

    if (typeof value !== 'object' || value === null) {
      // If value is a primitive, assign it directly
      currentTarget[lastKey] = value;
    } else {
      // If value is an object, merge it recursively
      if (!currentTarget[lastKey]) {
        currentTarget[lastKey] = {}; // Initialize if the path doesn't exist
      }
      this.updateNestedData(currentTarget[lastKey], value); // Recursively merge the object
    }
  }
}
